import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useOutputSummary } from '@/hooks/useOutput';
import { OutputStatCards } from '@/components/features/output/OutputStatCards';
import { CategoryMixChart } from '@/components/features/output/CategoryMixChart';
import { HygieneSection } from '@/components/features/output/HygieneSection';
import { WipTable } from '@/components/features/output/WipTable';
import { CATEGORY_DISPLAY, CATEGORY_ORDER } from '@/components/features/output/categories';
import { GlassCard } from '@/components/ui/GlassCard';
import { ErrorState } from '@/components/feedback/ErrorState';
import { PeriodToggle, type Period } from '@/components/ui/PeriodToggle';
import { TrendChart } from '@/components/charts/TrendChart';
import { periodAxisLabel } from '@/lib/aggregate';
import type { OutputPeriodBucket } from '@/lib/api';

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business プロジェクトアウトプットページ。QDT広義(QDT/SXT/PST/CST/CLT)のリリース実績を月次・四半期・期で振り返り、総量と一人当たりの推移・目的カテゴリ配分・必須項目の記入漏れを並べる
 * @graph-connects useOutputSummary [calls] アウトプット集計データフック
 * @graph-connects OutputStatCards [calls] サマリーカード描画
 * @graph-connects CategoryMixChart [calls] 目的カテゴリ配分描画
 * @graph-connects HygieneSection [calls] 記入漏れ描画
 * @graph-connects WipTable [calls] 仕掛かり案件一覧描画
 * @graph-connects TrendChart [calls] 推移描画
 * @graph-connects PeriodToggle [calls] 集計期間の切替
 * @graph-connects ErrorState [calls] エラー表示描画
 */
export const Route = createFileRoute('/output')({
  component: OutputPage,
});

/**
 * グラフに描く期間数。古い薄いバケット（1件だけの四半期など）まで描くと
 * 直近の増減が潰れて読めなくなるため、直近だけに絞る。表は全期間を出す。
 */
/** @graph-connects none */
const CHART_WINDOW: Record<Period, number> = { month: 12, quarter: 8, year: 5 };

/** @graph-connects none */
const CHART_WINDOW_LABEL: Record<Period, string> = {
  month: '直近12ヶ月',
  quarter: '直近8四半期',
  year: '直近5期',
};

/** 表の並びは新しい期間を上に置く。画面を開いた人が最初に見るのは直近のため */
/** @graph-connects none */
function descending(buckets: OutputPeriodBucket[]): OutputPeriodBucket[] {
  return [...buckets].reverse();
}

/** @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す */
function OutputPage() {
  const [period, setPeriod] = useState<Period>('quarter');
  const [mixMetric, setMixMetric] = useState<'pt' | 'count'>('pt');

  const summaryQuery = useOutputSummary(period);
  const summary = summaryQuery.data?.data ?? null;
  const buckets = summary?.buckets ?? [];
  const latest = buckets.length > 0 ? (buckets[buckets.length - 1] ?? null) : null;
  const previous = buckets.length > 1 ? (buckets[buckets.length - 2] ?? null) : null;

  const chartBuckets = buckets.slice(-CHART_WINDOW[period]);
  const countTrend = chartBuckets.map((bucket) => ({
    period: bucket.key,
    value: bucket.releaseCount,
  }));
  const ptTrend = chartBuckets.map((bucket) => ({ period: bucket.key, value: bucket.totalPt }));
  // 一人当たりは主担当ユニーク人数が分母。0人の期は点にしない（0除算で ∞ になるより空欄）
  const ptPerOwnerTrend = chartBuckets.map((bucket) => ({
    period: bucket.key,
    value: bucket.ptPerOwner,
  }));
  const releasesPerOwnerTrend = chartBuckets.map((bucket) => ({
    period: bucket.key,
    value: bucket.releasesPerOwner,
  }));
  // 主担当が空の案件は分子にだけ乗る。ただし実測では「記入漏れ」ではなく
  // 分割チケット（_chunk）に主担当を入れない運用によるもので、担当者は他の案件で
  // 既に分母に入っているため一人当たりはほとんど動かない（2026-09-25 実測）。
  // 誤読されないよう件数と理由をセットで画面に出す
  const ownerMissingTotal = chartBuckets.reduce((sum, b) => sum + b.ownerMissingCount, 0);
  const chartReleaseTotal = chartBuckets.reduce((sum, b) => sum + b.releaseCount, 0);

  return (
    <div style={{ animation: 'fadeIn 0.4s ease' }}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            プロジェクトアウトプット
          </h1>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            QDT広義（QDT / SXT / PST / CST / CLT）・aC Div
          </span>
          {summary && (
            <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
              集計: {new Date(summary.generatedAt).toLocaleString('ja-JP')}
            </span>
          )}
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      {summaryQuery.isError && (
        <ErrorState title="データ取得エラー" message="アウトプット集計の取得に失敗しました。" />
      )}

      {summaryQuery.isLoading && (
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          読み込み中...
        </p>
      )}

      {summary && (
        <div className="space-y-6">
          <OutputStatCards latest={latest} previous={previous} wip={summary.wip} />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <GlassCard>
              <p
                className="mb-3 text-sm font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                リリース数の推移（{CHART_WINDOW_LABEL[period]}）
              </p>
              <TrendChart
                data={countTrend}
                yLabel="リリース数"
                xLabel={periodAxisLabel(period)}
                unit="count"
              />
            </GlassCard>
            <GlassCard>
              <p
                className="mb-3 text-sm font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                総Ptの推移（{CHART_WINDOW_LABEL[period]}）
              </p>
              <TrendChart
                data={ptTrend}
                yLabel="総Pt"
                xLabel={periodAxisLabel(period)}
                unit="count"
              />
            </GlassCard>
            <GlassCard>
              <p
                className="mb-3 text-sm font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                一人当たりリリース数の推移（{CHART_WINDOW_LABEL[period]}）
              </p>
              <TrendChart
                data={releasesPerOwnerTrend}
                yLabel="件/人"
                xLabel={periodAxisLabel(period)}
                unit="count"
              />
            </GlassCard>
            <GlassCard>
              <p
                className="mb-3 text-sm font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                一人当たりPtの推移（{CHART_WINDOW_LABEL[period]}）
              </p>
              <TrendChart
                data={ptPerOwnerTrend}
                yLabel="Pt/人"
                xLabel={periodAxisLabel(period)}
                unit="count"
              />
            </GlassCard>
          </div>

          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            一人当たりの分母は<strong>その期に1件以上リリースした主担当のユニーク人数</strong>で、
            チームの在籍人数ではありません。人が抜けた期は分母が減るぶん一人当たりが上がります。
            {ownerMissingTotal > 0 && (
              <>
                {' '}
                この期間の {chartReleaseTotal} 件のうち{' '}
                <strong>{ownerMissingTotal} 件は主担当が空</strong>
                ですが、これは記入漏れではなく
                <strong>大型案件の分割チケット（_chunk）には主担当を入れない運用</strong>
                のためです。分割チケットの90%が空で、通常案件は18%。
                空の案件のPt・件数は分子に入り分母には反映されませんが、実測では
                それらを担当している人は他の案件で既に分母に入っており、
                一人当たりの値はほとんど動きません。
              </>
            )}
          </p>

          <div>
            <div className="mb-2 flex justify-end gap-2">
              <button
                type="button"
                aria-pressed={mixMetric === 'pt'}
                onClick={() => {
                  setMixMetric('pt');
                }}
                className="px-3 py-1 text-xs font-medium rounded-md"
                style={{
                  background: mixMetric === 'pt' ? 'var(--color-bg-surface)' : 'transparent',
                  color:
                    mixMetric === 'pt' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                }}
              >
                Pt
              </button>
              <button
                type="button"
                aria-pressed={mixMetric === 'count'}
                onClick={() => {
                  setMixMetric('count');
                }}
                className="px-3 py-1 text-xs font-medium rounded-md"
                style={{
                  background: mixMetric === 'count' ? 'var(--color-bg-surface)' : 'transparent',
                  color:
                    mixMetric === 'count' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                }}
              >
                件数
              </button>
            </div>
            <CategoryMixChart buckets={chartBuckets} metric={mixMetric} />
          </div>

          <GlassCard>
            <p
              className="mb-3 text-sm font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              期間別の実績
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left" aria-label="期間別の実績">
                <thead>
                  <tr>
                    {[
                      '期間',
                      'リリース数',
                      '総Pt',
                      '主担当',
                      '件/人',
                      'Pt/人',
                      '平均Pt',
                      'Pt未入力',
                      '最大カテゴリ',
                    ].map((header) => (
                      <th
                        key={header}
                        className="pb-2 pr-3 text-xs font-medium"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {descending(buckets).map((bucket) => {
                    const top = [...bucket.byCategory].sort((a, b) => b.totalPt - a.totalPt)[0];
                    return (
                      <tr key={bucket.key} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td
                          className="py-2 pr-3 text-xs whitespace-nowrap"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {bucket.label}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {bucket.releaseCount}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {bucket.totalPt.toLocaleString()}
                        </td>
                        {/* 一人当たりの分母。空の件数（＝分割チケット）を併記する */}
                        <td
                          className="py-2 pr-3 text-xs tabular-nums whitespace-nowrap"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {bucket.ownerCount}
                          {bucket.ownerMissingCount > 0 && (
                            <span style={{ color: 'var(--color-text-muted)' }}>
                              {' '}
                              (空 {bucket.ownerMissingCount})
                            </span>
                          )}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {bucket.releasesPerOwner ?? '—'}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {bucket.ptPerOwner?.toLocaleString() ?? '—'}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {bucket.avgPt ?? '—'}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs tabular-nums"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {bucket.ptMissingCount}
                        </td>
                        <td
                          className="py-2 pr-3 text-xs"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {top ? CATEGORY_DISPLAY[top.category].label : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              {CATEGORY_ORDER.map((category) => (
                <span
                  key={category}
                  className="flex items-center gap-1 text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: CATEGORY_DISPLAY[category].color }}
                  />
                  {CATEGORY_DISPLAY[category].label}
                </span>
              ))}
            </div>
          </GlassCard>

          <HygieneSection
            wip={summary.wip}
            releasedDateMissing={summary.releasedDateMissing}
            invalidReleaseDates={summary.invalidReleaseDates}
          />

          <WipTable items={summary.wip.items} />
        </div>
      )}
    </div>
  );
}
